import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const logoUrl = 'https://customer-assets.emergentagent.com/job_hugo-sites-pro/artifacts/hheqy0jw_logo.png';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  const handleWhatsAppClick = () => {
    const phone = '5521976614158';
    const message = 'Olá! Gostaria de saber mais sobre a criação da minha landing page profissional.';
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg py-4' : 'bg-transparent py-6'
      }`}
      role="banner"
    >
      <div className="container mx-auto lg:px-20">
        <nav className="flex items-center justify-between px-6" role="navigation" aria-label="Main navigation">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <img
              src={logoUrl}
              alt="Hugo Renzi - Landing Pages Profissionais"
              className={`h-10 w-auto transition-all duration-300 ${
                isScrolled ? 'brightness-0' : 'brightness-0 invert'
              }`}
              loading="eager"
            />
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('sobre')}
              className={`transition-colors font-medium ${
                isScrolled 
                  ? 'text-gray-600 hover:text-blue-600' 
                  : 'text-white hover:text-yellow-300'
              }`}
              aria-label="Ir para seção Sobre"
            >
              Sobre
            </button>
            <button
              onClick={() => scrollToSection('servicos')}
              className={`transition-colors font-medium ${
                isScrolled 
                  ? 'text-gray-600 hover:text-blue-600' 
                  : 'text-white hover:text-yellow-300'
              }`}
              aria-label="Ir para seção Serviços"
            >
              Serviços
            </button>
            <button
              onClick={() => scrollToSection('planos')}
              className={`transition-colors font-medium ${
                isScrolled 
                  ? 'text-gray-600 hover:text-blue-600' 
                  : 'text-white hover:text-yellow-300'
              }`}
              aria-label="Ir para seção Planos"
            >
              Planos
            </button>
            <button
              onClick={() => scrollToSection('faq')}
              className={`transition-colors font-medium ${
                isScrolled 
                  ? 'text-gray-600 hover:text-blue-600' 
                  : 'text-white hover:text-yellow-300'
              }`}
              aria-label="Ir para seção FAQ"
            >
              FAQ
            </button>
            <Button
              onClick={handleWhatsAppClick}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-300 hover:scale-105 ${
                isScrolled
                  ? 'bg-blue-600 hover:bg-blue-700 text-white border-2 border-blue-600'
                  : 'bg-transparent hover:bg-white/10 text-white border-2 border-white'
              }`}
              aria-label="Falar com Hugo no WhatsApp"
            >
              Falar com Hugo
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden transition-colors ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`md:hidden mt-4 pb-4 border-t pt-4 px-6 bg-white border-gray-200`}
            role="menu"
          >
            <div className="flex flex-col gap-4">
              <button
                onClick={() => scrollToSection('sobre')}
                className={`transition-colors font-medium text-left text-gray-600 hover:text-blue-600`}
                role="menuitem"
              >
                Sobre
              </button>
              <button
                onClick={() => scrollToSection('servicos')}
                className={`transition-colors font-medium text-left text-gray-600 hover:text-blue-600`}
                role="menuitem"
              >
                Serviços
              </button>
              <button
                onClick={() => scrollToSection('planos')}
                className={`transition-colors font-medium text-left text-gray-600 hover:text-blue-600`}
                role="menuitem"
              >
                Planos
              </button>
              <button
                onClick={() => scrollToSection('faq')}
                className={`transition-colors font-medium text-left text-gray-600 hover:text-blue-600`}
                role="menuitem"
              >
                FAQ
              </button>
              <Button
                onClick={handleWhatsAppClick}
                className={`w-full rounded-full font-medium bg-blue-600 hover:bg-blue-700 text-white`}
              >
                Falar com Hugo
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

export default Header;
